import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {PropensityScoringService} from '../../service/api/propensity-scoring.service';
import {PropensityScoringModelService} from '../../service/api/propensity-scoring-model.service';
import {PropensityScoringRunDto} from '../../model/dto/entity/propensity-scoring-run.dto';
import {
    CampaignScoreStats,
    MemberScoreDto,
    ScoreDistributionResponse,
    ScoringActionResponse,
    ScoringModelResponse
} from '../../model/dto/api/propensity-scoring.interface';

/**
 * Tabs enum (PrimeNG v20 – value driven)
 */
export enum PropensityTab {
    Overview = 'overview',
    Members = 'members',
    History = 'history'
}

@Component({
    selector: 'ct-propensity-scoring-manager',
    templateUrl: './propensity-scoring-manager.component.html',
    standalone: false
})
export class PropensityScoringManagerComponent implements OnChanges {

    /* ================= INPUT / OUTPUT ================= */

    @Input() campaignId!: number;
    @Input() campaignName!: string;

    @Output() scoringCompleted = new EventEmitter<ScoringActionResponse>();
    @Output() memberSelected = new EventEmitter<MemberScoreDto>();

    /* ================= TAB STATE ================= */

    readonly PropensityTab = PropensityTab;

    private _activeTab: PropensityTab = PropensityTab.Overview;

    get activeTab(): PropensityTab {
        return this._activeTab;
    }

    set activeTab(tab: PropensityTab) {
        this._activeTab = tab;

        // Lazy-load members tab
        if (tab === PropensityTab.Members && this.scoredMembers.length === 0) {
            this.loadScoredMembers();
        }
    }

    /* ================= DATA ================= */

    activeModels: ScoringModelResponse[] = [];
    selectedModelId!: number;

    scoringRuns: PropensityScoringRunDto[] = [];
    latestRun!: PropensityScoringRunDto;

    scoreDistribution!: ScoreDistributionResponse;
    campaignStats!: CampaignScoreStats;

    scoredMembers: MemberScoreDto[] = [];

    /* ================= UI STATE ================= */

    loading = false;
    scoringInProgress = false;
    error = '';

    selectedTierFilter = '';
    sortDescending = true;

    tierFilters = [
        { value: '', label: 'Alle Stufen' },
        { value: 'HIGH', label: 'Hohe Stufe' },
        { value: 'MEDIUM', label: 'Mittlere Stufe' },
        { value: 'LOW', label: 'Niedrige Stufe' }
    ];

    constructor(
        private scoringService: PropensityScoringService,
        private modelService: PropensityScoringModelService
    ) {}

    /* ================= LIFECYCLE ================= */

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['campaignId'] && this.campaignId) {
            this.loadData();
        }
    }

    /* ================= DATA LOADING ================= */

    loadData(): void {
        this.loading = true;
        this.error = '';

        this.loadActiveModels();
        this.loadScoringRuns();
        this.loadCampaignStats();
    }

    loadActiveModels(): void {
        this.modelService.getActiveModels().subscribe({
            next: models => {
                this.activeModels = models;
                if (models.length && !this.selectedModelId) {
                    this.selectedModelId = models[0].id;
                }
            },
            error: err => console.error(err)
        });
    }

    loadScoringRuns(): void {
        this.scoringService.getRunsForCampaign(this.campaignId).subscribe({
            next: runs => {
                this.scoringRuns = runs;
                if (runs.length) {
                    this.latestRun = runs[0];
                    this.loadScoreDistribution(this.latestRun.id);
                }
                this.loading = false;
            },
            error: () => {
                this.error = 'Bewertungsläufe konnten nicht geladen werden';
                this.loading = false;
            }
        });
    }

    loadScoreDistribution(runId: number): void {
        this.scoringService.getScoreDistribution(runId).subscribe({
            next: d => (this.scoreDistribution = d),
            error: err => console.error(err)
        });
    }

    loadCampaignStats(): void {
        this.scoringService.getCampaignScoreStats(this.campaignId).subscribe({
            next: stats => (this.campaignStats = stats),
            error: err => console.error(err)
        });
    }

    loadScoredMembers(): void {
        this.loading = true;

        this.scoringService.getMembersSortedByScore(
            this.campaignId,
            this.selectedTierFilter || undefined,
            this.sortDescending
        ).subscribe({
            next: members => {
                this.scoredMembers = members;
                this.loading = false;
            },
            error: () => {
                this.error = 'Bewertete Mitglieder konnten nicht geladen werden';
                this.loading = false;
            }
        });
    }

    /* ================= ACTIONS ================= */

    executeScoring(): void {
        if (!this.selectedModelId) {
            this.error = 'Bitte wählen Sie ein Bewertungsmodell aus';
            return;
        }

        this.scoringInProgress = true;

        this.scoringService.executeScoring(this.campaignId, this.selectedModelId).subscribe({
            next: response => {
                this.scoringInProgress = false;
                if (response.success) {
                    this.loadScoringRuns();
                    this.loadCampaignStats();
                    this.scoringCompleted.emit(response);
                } else {
                    this.error = response.message;
                }
            },
            error: () => {
                this.scoringInProgress = false;
                this.error = 'Bewertung konnte nicht ausgeführt werden';
            }
        });
    }

    toggleSortOrder(): void {
        this.sortDescending = !this.sortDescending;
        this.loadScoredMembers();
    }

    onTierFilterChange(): void {
        this.loadScoredMembers();
    }

    onMemberClick(member: MemberScoreDto): void {
        this.memberSelected.emit(member);
    }

    /* ================= HELPERS ================= */

    formatScore(score: number): string {
        return score == null ? '-' : `${(score * 100).toFixed(1)}%`;
    }

    formatDate(date: Date): string {
        return date ? new Date(date).toLocaleString() : '-';
    }

    getRunStatusClass(status: string): string {
        return {
            COMPLETED: 'text-green-600 bg-green-100',
            RUNNING: 'text-blue-600 bg-blue-100',
            FAILED: 'text-red-600 bg-red-100'
        }[status?.toUpperCase()] || 'text-gray-600 bg-gray-100';
    }

    clearError(): void { this.error = ''; }
}
